import { useState } from "react";
import { MailWarning, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Gentle, dismissible nudge for users who haven't confirmed their email.
export default function VerifyBanner() {
  const { user, resendVerification } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user || user.email_verified || dismissed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await resendVerification();
      toast.success("Verification email sent — check your inbox.");
    } catch {
      toast.error("Could not send right now. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-6 glass-card border-[#e6c67a]/30 p-4 flex items-center gap-3"
      data-testid="verify-email-banner"
    >
      <MailWarning className="h-5 w-5 text-[#e6c67a] shrink-0" strokeWidth={1.75} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 font-medium">Please confirm your email</div>
        <div className="text-[12px] text-zinc-400 font-light">
          We sent a verification link to <span className="text-zinc-200">{user.email}</span>. Confirm it to secure your account.
        </div>
      </div>
      <button
        onClick={resend}
        disabled={busy}
        className="btn-glass text-white px-3.5 py-2 text-xs font-medium whitespace-nowrap disabled:opacity-60 inline-flex items-center gap-1.5"
        data-testid="verify-resend-btn"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Resend email
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="text-zinc-500 hover:text-white transition-colors"
        aria-label="Dismiss"
        data-testid="verify-dismiss-btn"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
